// v2 barrel cactus — a fat ribbed keg: a squashed globe core wrapped in a ring of
// eight bulging rib lobes whose shoulders lean in toward a pale woolly crown
// disc, straw spines bristling out of the ribs, and a ring of cup blooms around
// the crown. Variants spin the rib cage, re-deal the spines and recolour the
// bloom ring (fire orange / desert yellow / dawn pink). Ported 1:1 from
// age-of-mcp's v2/cactus-barrel.ts, using the library's shared `tropic` (h01,
// lerp, tube) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const FLESH = "#4c8a50";
const RIB = "#3c7842";
const RIB_2 = "#356d3b";
const WOOL = "#ddd2a8";
const SPINE = "#e9ddb4";
const BLOOMS = ["#e07338", "#e3b73c", "#d96d92"] as const;

function barrelParts(v: number): Part[] {
  const seed = v * 29 + 7;
  const yaw = v * 2.4 + tropic.h01(seed, 0) * 0.8;
  const parts: Part[] = [
    // squashed globe core
    { geometry: new THREE.IcosahedronGeometry(0.235, 0), color: FLESH,
      position: [0, 0.26, 0], rotation: [0, yaw, 0], scale: [1, 0.92, 1] },
    // pale woolly crown disc
    { geometry: new THREE.CylinderGeometry(0.085, 0.115, 0.055, 7),
      color: WOOL, position: [0, 0.455, 0] },
  ];
  // Eight bulging rib lobes, shoulders leaning in toward the crown.
  for (let i = 0; i < 8; i++) {
    const a = yaw + (i / 8) * Math.PI * 2;
    parts.push({
      geometry: new THREE.CylinderGeometry(0.045, 0.062, 0.34, 5),
      color: i % 2 ? RIB : RIB_2,
      position: [Math.cos(a) * 0.185, 0.24, Math.sin(a) * 0.185],
      rotation: [Math.sin(a) * -0.3, 0, Math.cos(a) * 0.3],
    });
  }
  // Straw spines bristling out and down from the ribs.
  for (let i = 0; i < 6; i++) {
    const a = yaw + (i / 6) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.7;
    const sy = tropic.lerp(0.16, 0.34, tropic.h01(seed, 20 + i));
    parts.push(tropic.tube(
      [Math.cos(a) * 0.2, sy, Math.sin(a) * 0.2],
      [Math.cos(a) * 0.31, sy - 0.05, Math.sin(a) * 0.31],
      0.009, 0.002, SPINE, 4));
  }
  // Cup blooms ringing the woolly crown.
  const bloom = BLOOMS[v % BLOOMS.length]!;
  for (let i = 0; i < 3; i++) {
    const a = yaw + (i / 3) * Math.PI * 2 + tropic.h01(seed, 30 + i) * 0.4;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.05, 0),
      color: bloom,
      position: [Math.cos(a) * 0.075, 0.505, Math.sin(a) * 0.075],
      scale: [1, 0.85, 1] });
  }
  return parts;
}

export const cactusBarrelV2 = defineModel({
  id: "cactus-barrel-v2",
  batchId: "fable",
  name: "Cactus barrel (v2)",
  tags: ["scenery","cactus","barrel","low-poly","age-of-mcp","fable"],
  summary: "Low-poly cactus barrel.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.3,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => barrelParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => barrelParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => barrelParts(2) },
  ],
  animations: [],
});
